/**
 * Curve maths for the strands.
 *
 * A real dendrite is thick where it leaves the soma and vanishingly thin at the
 * synapse, and it never travels in a straight line. Both properties carry
 * meaning here: thickness is confidence, and the wander is what makes a
 * hundred strands legible as separate paths instead of a starburst.
 *
 * So strands are not stroked — they are *filled polygons* whose half-width is a
 * function of arc position. That is the only way to taper in Canvas 2D.
 */

export interface Pt {
  x: number
  y: number
}

export const TAU = Math.PI * 2

export const clamp = (v: number, lo = 0, hi = 1) => (v < lo ? lo : v > hi ? hi : v)
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t
export const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
export const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5)
export const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

/** Overshoot-and-settle. Used where something should feel like it *arrived*. */
export function easeOutBack(t: number, overshoot = 1.9): number {
  const c3 = overshoot + 1
  return 1 + c3 * Math.pow(t - 1, 3) + overshoot * Math.pow(t - 1, 2)
}

/** Hermite ramp between two edges — a soft threshold with no visible corner. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0 || 1e-6))
  return t * t * (3 - 2 * t)
}

/**
 * Saltatory advance — the way a growth cone actually moves.
 *
 * Neurites do not extend at constant speed: they surge, stall while the cone
 * samples its surroundings, surge again. This is a strictly monotonic
 * reparameterisation of t (the integral of a positive cosine ripple), so the
 * tip never travels backwards — it just keeps changing its mind about pace.
 * Deterministic in `seed`, so a given strand always grows the same way.
 */
export function saltatory(t: number, seed: number): number {
  const bursts = 3 + Math.floor(seed * 3)
  const amp = 0.62
  const phase = seed * TAU
  const ripple = (amp / (TAU * bursts)) * (Math.sin(TAU * bursts * t + phase) - Math.sin(phase))
  const u = clamp(t + ripple)
  // Gentle ease at both ends: the cone starts from rest and settles on target.
  return u * u * (3 - 2 * u)
}

/** Deterministic 0–1 hash of a string. Same node, same wander, every reload. */
export function hash01(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 100000) / 100000
}

/** Seeded PRNG — layout must be stable across renders. */
export function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function cubicAt(p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): Pt {
  const u = 1 - t
  const a = u * u * u
  const b = 3 * u * u * t
  const c = 3 * u * t * t
  const d = t * t * t
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  }
}

/**
 * Sample a parent→child strand.
 *
 * Control points bow the curve *outward along the radius* so branches fan like
 * a dendritic tree rather than crossing the middle. A low-frequency sine
 * wander, seeded per-strand, keeps neighbouring strands from reading as one.
 */
export function strandPath(from: Pt, to: Pt, seed: number, steps = 26): Pt[] {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const len = Math.hypot(dx, dy) || 1
  // Unit normal, for the lateral bow.
  const nx = -dy / len
  const ny = dx / len

  // Bow direction and magnitude are seeded, so a strand always wanders the
  // same way. Scaled by length so short links stay tight.
  const bow = (seed - 0.5) * 2 * Math.min(len * 0.19, 64)
  const c1: Pt = {
    x: from.x + dx * 0.34 + nx * bow * 0.85,
    y: from.y + dy * 0.34 + ny * bow * 0.85,
  }
  const c2: Pt = { x: from.x + dx * 0.7 + nx * bow * 0.35, y: from.y + dy * 0.7 + ny * bow * 0.35 }

  // A second, faster wander applied to the sampled points — the micro-tremor
  // that separates a drawn line from a plotted one.
  const amp = Math.min(len * 0.028, 5.2)
  const phase = seed * TAU
  const freq = 2.2 + seed * 2.6

  const out: Pt[] = new Array(steps + 1)
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const p = cubicAt(from, c1, c2, to, t)
    // Taper the tremor to zero at both endpoints so joints stay clean.
    const env = Math.sin(t * Math.PI)
    const w = Math.sin(phase + t * freq * Math.PI) * amp * env
    out[i] = { x: p.x + nx * w, y: p.y + ny * w }
  }
  return out
}

/**
 * Build the outline of a tapered ribbon along `pts`.
 * `widthAt(t)` returns the half-width at arc fraction t.
 * Returns a closed polygon: forward along one offset, back along the other.
 */
export function ribbon(pts: Pt[], widthAt: (t: number) => number): Pt[] {
  const n = pts.length
  const left: Pt[] = new Array(n)
  const right: Pt[] = new Array(n)

  for (let i = 0; i < n; i++) {
    const prev = pts[Math.max(0, i - 1)]!
    const next = pts[Math.min(n - 1, i + 1)]!
    let tx = next.x - prev.x
    let ty = next.y - prev.y
    const m = Math.hypot(tx, ty) || 1
    tx /= m
    ty /= m
    const w = widthAt(i / (n - 1))
    const nx = -ty * w
    const ny = tx * w
    const p = pts[i]!
    left[i] = { x: p.x + nx, y: p.y + ny }
    right[i] = { x: p.x - nx, y: p.y - ny }
  }

  const poly: Pt[] = []
  for (let i = 0; i < n; i++) poly.push(left[i]!)
  for (let i = n - 1; i >= 0; i--) poly.push(right[i]!)
  return poly
}

/**
 * A copy of a path pushed sideways by a per-position offset.
 *
 * Uses the same finite-difference tangent as `ribbon` above, so an offset path and
 * a ribbon built from the same points stay parallel — a different tangent estimate
 * would make the two drift apart at the curves, which is exactly where a split
 * strand is most visible.
 */
export function offsetPath(pts: Pt[], offsetAt: (t: number) => number): Pt[] {
  const n = pts.length
  if (n < 2) return pts.slice()
  const out: Pt[] = new Array(n)
  for (let i = 0; i < n; i++) {
    const prev = pts[Math.max(0, i - 1)]!
    const next = pts[Math.min(n - 1, i + 1)]!
    let tx = next.x - prev.x
    let ty = next.y - prev.y
    const m = Math.hypot(tx, ty) || 1
    tx /= m
    ty /= m
    const d = offsetAt(i / (n - 1))
    const p = pts[i]!
    out[i] = { x: p.x - ty * d, y: p.y + tx * d }
  }
  return out
}

export function tracePoly(ctx: CanvasRenderingContext2D, poly: Pt[]) {
  if (!poly.length) return
  ctx.beginPath()
  ctx.moveTo(poly[0]!.x, poly[0]!.y)
  for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i]!.x, poly[i]!.y)
  ctx.closePath()
}

/** Cumulative arc lengths, normalised 0–1. Lets particles move at even speed. */
export function arcTable(pts: Pt[]): number[] {
  const t: number[] = [0]
  let acc = 0
  for (let i = 1; i < pts.length; i++) {
    acc += Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y)
    t.push(acc)
  }
  if (acc > 0) for (let i = 0; i < t.length; i++) t[i]! /= acc
  return t
}

/** Point at normalised arc position `s` along `pts`, using its arc table. */
export function pointAtArc(pts: Pt[], table: number[], s: number): Pt {
  const c = clamp(s)
  let lo = 0
  let hi = table.length - 1
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (table[mid]! < c) lo = mid
    else hi = mid
  }
  const t0 = table[lo]!
  const t1 = table[hi]!
  const f = t1 - t0 > 1e-6 ? (c - t0) / (t1 - t0) : 0
  const a = pts[lo]!
  const b = pts[hi]!
  return { x: lerp(a.x, b.x, f), y: lerp(a.y, b.y, f) }
}

/** Distance from a point to a polyline — used for edge hit-testing. */
export function distToPolyline(pts: Pt[], px: number, py: number): number {
  let best = Infinity
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!
    const b = pts[i]!
    const dx = b.x - a.x
    const dy = b.y - a.y
    const l2 = dx * dx + dy * dy
    let t = l2 ? ((px - a.x) * dx + (py - a.y) * dy) / l2 : 0
    t = clamp(t)
    const d = Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy))
    if (d < best) best = d
  }
  return best
}
